/** @format */

import {
  Suspense,
  Fragment,
  lazy,
  createContext,
  useContext,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode
} from 'react';
import { Routes, Route, Navigate, useLocation, type Location } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { DefaultLayout } from 'src/components';
import HomeView from 'src/views/home/HomeView';
import LoadingScreen from 'src/components/LoadingScreen';
import { Profile } from './views/portfolio/profile';
import { PersonalData } from './views/portfolio/personaldata';
import { LegalLayout } from 'src/views/legal-pages/legaLayout';
import { Access } from './views/portfolio/access';
import Components from './views/components';
import KnowledgeBase from './views/knowledgeBase';
import KnowledgeBaseDetail from './views/knowledgeBase/detail';
import OnboardingPageView from 'src/views/onboarding/OnboardingPageView';
import DashboardByIdView from 'src/views/home/DashboardByIdView';
import { ONBOARDING_PAGE_ID, ROLES } from 'src/constants';
import EventsPage from './views/events';
import ExtranetPage from './views/extranet';
import ExtranetContentDetail from './views/extranet/contentDetail';
import ExtranetContentDrawer from './views/extranet/contentDrawer';
import ContentEditor from './views/extranet/ContentEditor';
import ContentPreview from './views/extranet/contentPreview';
import AdministrationPage from './views/administration';
import TagsAdminPage from './views/administration/TagsAdminPage';
import NewsPage from './views/news/list';
import NewsDetail from './views/news/detail';
import NewsWidgetPreview from './views/widgets/newsWidgetPreview';
import WidgetPreview from './views/widgets';
import EventsWidgetPreview from './views/widgets/eventsWidgetPreview';
import ExtranetWidgetPreview from './views/widgets/extranetWidgetPreview';
import SearchPage from './views/search/searchView';
import TopicsAdminPage from './views/administration/TopicsAdminPage';
import TopicEditor from './views/administration/TopicEditor';
import ExtranetContentsAdminPage from './views/administration/ExtranetContentsAdminPage';
import RoleGuard from 'src/components/RoleGuard';
import AcademyWidgetPreview from './views/widgets/academyWidgetPreview';
import WebshopWidgetPreview from './views/widgets/webshopWidgetPreview';
import TranslationsExport from './views/translationsExport';
import { getRouteDisplayUpdateType, startViewTransition } from 'src/utils/viewTransition';

export interface AppRoute {
  path?: string;
  redirect?: string;
  layout?: ComponentType<{ children?: ReactNode }>;
  guard?: ComponentType<{ children?: ReactNode }>;
  component?: ComponentType<any>;
  routes?: AppRoute[];
}

interface RouteLocationState {
  backgroundLocation?: Location;
  openAsDrawer?: boolean;
}

interface RouteLocationContextValue {
  currentLocation: Location;
  switchLocation: Location;
}

const RouteLocationContext = createContext<RouteLocationContextValue | null>(null);

function RouteElement({ route }: { route: AppRoute }) {
  const Guard = route.guard || Fragment;
  const Layout = route.layout || Fragment;
  const Component = route.component;

  if (route.routes) {
    return (
      <Guard>
        <Layout>
          <RoutesRenderer routes={route.routes} />
        </Layout>
      </Guard>
    );
  }

  if (route.redirect) {
    return (
      <Guard>
        <Layout>
          <Navigate to={route.redirect} replace />
        </Layout>
      </Guard>
    );
  }

  return (
    <Guard>
      <Layout>
        {Component ? <Component /> : null}
      </Layout>
    </Guard>
  );
}

function RoutesRenderer({ routes }: { routes: AppRoute[] }) {
  const location = useLocation();
  const parentContext = useContext(RouteLocationContext);
  const isRoot = parentContext == null;

  const liveCurrent = isRoot ? location : parentContext.currentLocation;
  const liveState = liveCurrent.state as RouteLocationState | null;
  const liveSwitch = isRoot ? liveState?.backgroundLocation || liveCurrent : parentContext.switchLocation;

  const [rootDisplayCurrent, setRootDisplayCurrent] = useState(liveCurrent);
  const [rootDisplaySwitch, setRootDisplaySwitch] = useState(liveSwitch);

  useEffect(() => {
    if (!isRoot) {
      return;
    }

    const updateType = getRouteDisplayUpdateType(
      liveSwitch,
      liveCurrent,
      rootDisplaySwitch,
      rootDisplayCurrent
    );

    if (updateType === 'none') {
      return;
    }

    if (updateType === 'sync') {
      flushSync(() => {
        setRootDisplaySwitch(liveSwitch);
        setRootDisplayCurrent(liveCurrent);
      });
      window.scrollTo(0, 0);
      return;
    }

    startViewTransition(() => {
      flushSync(() => {
        setRootDisplaySwitch(liveSwitch);
        setRootDisplayCurrent(liveCurrent);
      });
      window.scrollTo(0, 0);
    });
  }, [isRoot, liveCurrent, liveSwitch, rootDisplayCurrent, rootDisplaySwitch]);

  const currentLocation = isRoot ? rootDisplayCurrent : liveCurrent;
  const switchLocation = isRoot ? rootDisplaySwitch : liveSwitch;
  const contextValue = { currentLocation, switchLocation };
  const state = currentLocation.state as RouteLocationState | null;

  return (
    <RouteLocationContext.Provider value={contextValue}>
      <Routes location={switchLocation}>
        {routes.map((route, i) => {
          const key = route.path || `route-${i}`;

          if (!route.path) {
            if (route.redirect) {
              return <Route key={key} path="*" element={<Navigate to={route.redirect} replace />} />;
            }

            if (route.component) {
              const FallbackComponent = route.component;
              return <Route key={key} path="*" element={<FallbackComponent />} />;
            }

            return null;
          }

          return <Route key={key} path={route.path} element={<RouteElement route={route} />} />;
        })}
      </Routes>
      {state?.backgroundLocation && state?.openAsDrawer ? (
        <Routes>
          <Route path="/extranet/content/:contentID/:locale" element={<ExtranetContentDrawer />} />
        </Routes>
      ) : null}
    </RouteLocationContext.Provider>
  );
}

export const renderRoutes = (routeConfig: AppRoute[] = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <RoutesRenderer routes={routeConfig} />
  </Suspense>
);

const routes: AppRoute[] = [
  {
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    path: `/page/${ONBOARDING_PAGE_ID}`,
    layout: DefaultLayout,
    component: OnboardingPageView
  },
  {
    path: '/page/:pageId',
    layout: DefaultLayout,
    component: LegalLayout
  },
  {
    path: '/page/:type/:pageId',
    layout: DefaultLayout,
    component: LegalLayout
  },
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '*',
    layout: DefaultLayout,
    routes: [
      {
        path: '/dashboard/:dashboardId',
        component: DashboardByIdView
      },
      {
        path: '/dashboard',
        component: HomeView
      },
      {
        path: '/profile',
        component: Profile
      },
      {
        path: '/personaldata',
        component: PersonalData
      },
      {
        path: '/access',
        component: Access
      },
      {
        path: '/knowledge-base/:kb/article/:nid',
        component: KnowledgeBaseDetail
      },
      {
        path: '/knowledge-base/:kb',
        component: KnowledgeBase
      },
      {
        path: '/search',
        component: SearchPage
      },
      {
        path: '/news/:nid',
        component: NewsDetail
      },
      {
        path: '/news',
        component: NewsPage
      },
      {
        path: '/events',
        component: EventsPage
      },
      {
        path: '/components',
        component: Components
      },
      {
        path: '/widgets-preview',
        component: WidgetPreview
      },
      {
        path: '/widgets-preview/news',
        component: NewsWidgetPreview
      },
      {
        path: '/widgets-preview/events',
        component: EventsWidgetPreview
      },
      {
        path: '/widgets-preview/extranet',
        component: ExtranetWidgetPreview
      },
      {
        path: '/widgets-preview/academy',
        component: AcademyWidgetPreview
      },
      {
        path: '/widgets-preview/webshop',
        component: WebshopWidgetPreview
      },
      {
        path: '/',
        redirect: '/dashboard'
      },
      {
        path: '/administration/content/create',
        component: () => (
          <RoleGuard role={ROLES.CONTENT_EDITOR}>
            <ContentEditor />
          </RoleGuard>
        )
      },
      {
        path: '/administration/content/preview',
        component: () => (
          <RoleGuard role={ROLES.CONTENT_EDITOR}>
            <ContentPreview />
          </RoleGuard>
        )
      },
      {
        path: '/extranet/content/:contentID/edit/:routeLocale',
        component: () => (
          <RoleGuard role={ROLES.CONTENT_EDITOR}>
            <ContentEditor />
          </RoleGuard>
        )
      },
      {
        path: '/extranet/content/:contentID/edit',
        component: () => (
          <RoleGuard role={ROLES.CONTENT_EDITOR}>
            <ContentEditor />
          </RoleGuard>
        )
      },
      {
        path: '/extranet/content/:contentID/:locale',
        component: ExtranetContentDetail
      },
      {
        path: '/extranet/content/:contentID',
        component: ExtranetContentDetail
      },
      {
        path: '/extranet/:topicID?',
        component: ExtranetPage
      },
      {
        path: '/administration',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <AdministrationPage />
          </RoleGuard>
        )
      },
      {
        path: '/administration/tags',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <TagsAdminPage />
          </RoleGuard>
        )
      },
      {
        path: '/administration/topics',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <TopicsAdminPage />
          </RoleGuard>
        )
      },
      {
        path: '/administration/topics/create',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <TopicEditor />
          </RoleGuard>
        )
      },
      {
        path: '/administration/topics/:topicID/:routeLocale',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <TopicEditor />
          </RoleGuard>
        )
      },
      {
        path: '/administration/topics/:topicID',
        component: () => (
          <RoleGuard role={ROLES.ADMIN}>
            <TopicEditor />
          </RoleGuard>
        )
      },
      {
        path: '/administration/contents',
        component: () => (
          <RoleGuard role={ROLES.CONTENT_EDITOR}>
            <ExtranetContentsAdminPage />
          </RoleGuard>
        )
      },
      {
        path: '/translations-export',
        component: TranslationsExport
      },
      {
        redirect: '/404'
      }
    ]
  }
];

export default routes;
